import React, { useState } from 'react';
import { FileText, Target, MonitorSmartphone, Share2, CalendarCheck, Radio, MessageCircle, Sparkles, ArrowUpRight, TrendingUp } from 'lucide-react';
import { capabilities, capabilityImages, capabilityMeta, capabilityDetails } from '../../mock';
import CapabilityDetailModal from './CapabilityDetailModal';

const iconMap = { FileText, Target, MonitorSmartphone, Share2, CalendarCheck, Radio, MessageCircle, Sparkles };

export default function Capabilities() {
  const [activeId, setActiveId] = useState(null);
  const activeCapability = activeId ? capabilities.find((c) => c.id === activeId) : null;
  const activeDetail = activeId ? capabilityDetails[activeId] : null;

  return (
    <section id="capabilities" className="py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-20 items-end">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Core Capabilities</span>
            </div>
            <p className="text-white/60 text-[15px] leading-relaxed max-w-sm mt-6">
              Eight services under one accountable partner — from top-of-funnel awareness to BANT-qualified booked meetings. Click any capability to see how it works.
            </p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="text-4xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] font-medium">
              Full-funnel demand generation, <span className="font-serif italic font-normal text-[#4ECDC4]">delivered at scale.</span>
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilities.map((c, idx) => {
            const Icon = iconMap[c.icon] || FileText;
            const meta = capabilityMeta[c.id] || { kpi: '', tags: [] };
            const isHighlight = idx === 1 || idx === 4;
            const accent = isHighlight ? '#FFB020' : '#4ECDC4';
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveId(c.id)}
                data-testid={`capability-card-${c.id}`}
                className="group relative bg-[#12234C] rounded-2xl overflow-hidden hover:bg-[#173070] transition-colors duration-500 cursor-pointer reveal border border-white/5 hover:border-white/25 text-left flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ECDC4]/60"
              >
                {/* image thumbnail */}
                <div className="relative h-44 lg:h-52 overflow-hidden">
                  <img
                    src={capabilityImages[c.id]}
                    alt={c.title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12234C] via-[#12234C]/40 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0F1C40]/40" />

                  <div
                    className="absolute top-4 left-4 w-11 h-11 rounded-xl backdrop-blur-md border flex items-center justify-center"
                    style={{ background: 'rgba(10,20,50,0.7)', borderColor: `${accent}55`, color: accent }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </div>

                  <div className="absolute top-4 right-4 flex items-center gap-2 text-[10px] font-mono text-white/60">
                    <span className="text-white/40">{String(idx + 1).padStart(2, '0')} / 08</span>
                  </div>

                  <div
                    className="absolute bottom-4 left-4 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full backdrop-blur-md border"
                    style={{ background: 'rgba(10,20,50,0.75)', borderColor: `${accent}55`, color: accent }}
                  >
                    <TrendingUp className="w-3 h-3" />
                    <span>{meta.kpi}</span>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-serif italic font-normal mb-2">{c.title}</h3>
                  <p className="text-[13px] text-white/70 leading-relaxed flex-1">{c.desc}</p>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {meta.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[9px] font-mono uppercase tracking-widest text-white/60 border border-white/10 px-2 py-1 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                    <span
                      className="text-[11px] font-mono uppercase tracking-wider transition-colors"
                      style={{ color: accent }}
                    >
                      Learn more
                    </span>
                    <ArrowUpRight
                      className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
                      style={{ color: accent }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <CapabilityDetailModal
        open={!!activeId}
        onClose={() => setActiveId(null)}
        detail={activeDetail}
        capabilityTitle={activeCapability?.title}
      />
    </section>
  );
}
